import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { impersonationAPI, employeeAPI } from '../services/api';

interface User {
  id: number;
  nome: string;
  cognome: string;
  email: string;
  role: string;
  is_super_admin?: boolean;
}

interface Employee {
  employee_id: number;
  user_id: number;
  full_name: string;
  matricola?: string | null;
}

interface ImpersonationSession {
  id: number;
  original_user_id: number;
  target_user_id: number;
  original_user_name: string;
  original_user_surname: string;
  target_user_name: string;
  target_user_surname: string;
  started_at: string;
  ended_at?: string | null;
}

const ImpersonationManager: React.FC = () => {
  const { user } = useAuth();
  const { isSuperAdmin, isImpersonating, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [impersonationDialogOpen, setImpersonationDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [currentSession, setCurrentSession] = useState<ImpersonationSession | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');

  useEffect(() => {
    if (isSuperAdmin) {
      loadCurrentSession();
      loadUsers();
      loadEmployees();
    }
  }, [user, isSuperAdmin]);

  const loadCurrentSession = async () => {
    try {
      const response = await impersonationAPI.getImpersonationContext();
      if (response.active && response.session) {
        setCurrentSession(response.session);
      } else {
        setCurrentSession(null);
      }
    } catch (error) {
      // Errore caricamento sessione
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch('/api/admin/users/all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      // Errore caricamento utenti
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await employeeAPI.searchEmployees({});
      setEmployees(data.employees || []);
    } catch (error) {
      // Errore caricamento dipendenti
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedUserId) return;

    setLoading(true);
    setError(null);
    
    // Salva IMMEDIATAMENTE l'ID dell'utente impersonato in localStorage PRIMA della chiamata API
    // Questo garantisce che anche se l'API fallisce, l'impersonazione può essere rilevata
    localStorage.setItem('impersonated_user_id', selectedUserId.toString());
    
    // Salva anche le informazioni della sessione per uso futuro (nel caso l'API fallisca)
    const targetUser = users.find(u => u.id === selectedUserId) || employees.find(e => e.user_id === selectedUserId);
    if (targetUser) {
      // Gestisci sia User che Employee
      let targetName = '';
      let targetSurname = '';
      if ('nome' in targetUser) {
        // È un User
        targetName = targetUser.nome || '';
        targetSurname = targetUser.cognome || '';
      } else if ('full_name' in targetUser) {
        // È un Employee - estrai nome e cognome da full_name
        const nameParts = targetUser.full_name.split(' ');
        targetSurname = nameParts[0] || '';
        targetName = nameParts.slice(1).join(' ') || '';
      }
      
      const sessionBackup = {
        target_user_name: targetName,
        target_user_surname: targetSurname,
        original_user_name: user?.nome || '',
        original_user_surname: user?.cognome || '',
      };
      localStorage.setItem('impersonation_session_backup', JSON.stringify(sessionBackup));
    }
    
    try {
      await impersonationAPI.startImpersonation(selectedUserId);
      
      setSuccess('Impersonazione avviata con successo!');
      setImpersonationDialogOpen(false);
      setSelectedUserId(null);
      
      // Ricarica la pagina per applicare il nuovo contesto
      // Aumenta il timeout per assicurarsi che localStorage sia persistito
      setTimeout(() => {
        const finalVerify = localStorage.getItem('impersonated_user_id');
        if (!finalVerify) {
          // Riprova a salvarlo
          localStorage.setItem('impersonated_user_id', selectedUserId.toString());
        }
        window.location.reload();
      }, 500);
    } catch (err: any) {
      // Anche se l'API fallisce, mantieni impersonated_user_id in localStorage
      // perché potrebbe essere un problema temporaneo o un problema di permessi
      const verifyId = localStorage.getItem('impersonated_user_id');
      if (!verifyId) {
        // Se per qualche motivo è stato rimosso, riprova a salvarlo
        localStorage.setItem('impersonated_user_id', selectedUserId.toString());
      }
      setError(err.response?.data?.error || err.message || 'Errore durante l\'impersonazione');
      // Non ricaricare la pagina se c'è un errore, lascia che l'utente veda il messaggio di errore
    } finally {
      setLoading(false);
    }
  };

  const handleEndImpersonation = async () => {
    setLoading(true);
    setError(null);
    try {
      await impersonationAPI.endImpersonation();
      setSuccess('Impersonazione terminata con successo!');
      setCurrentSession(null);
      // Ricarica la pagina
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante la terminazione');
    } finally {
      setLoading(false);
    }
  };

  // Filtra utenti
  const filteredUsers = users.filter(u => {
    const matchesSearch = searchTerm === '' || 
      `${u.nome} ${u.cognome}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Crea mappa user_id -> employee
  const userToEmployeeMap = new Map<number, Employee>();
  employees.forEach(emp => {
    if (emp.user_id) {
      userToEmployeeMap.set(emp.user_id, emp);
    }
  });

  // L'impersonazione è riservata SOLO ai super admin originali (non impersonati)
  // Durante l'impersonazione, isSuperAdmin è false anche se l'utente impersonato fosse super admin
  if (!isSuperAdmin) {
    // Per gli amministratori non super admin passa sempre dalla root:
    // la landing corretta viene risolta centralmente da NavigateToDashboard.
    if (isAdmin) {
      navigate('/', { replace: true });
      return null;
    }
    
    // Se non è admin, mostra messaggio informativo
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Accesso negato. Solo i super admin possono accedere a questa funzionalità.
          </Typography>
          {isImpersonating && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Stai visualizzando come utente impersonato. L'impersonazione è disponibile solo per i super admin originali.
            </Typography>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Gestione accessi utente
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadCurrentSession();
              loadUsers();
            }}
          >
            Aggiorna
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Sessione corrente */}
        {currentSession && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              ⚠️ Impersonazione Attiva
            </Typography>
            <Typography variant="body2" gutterBottom>
              Stai visualizzando come: <strong>{currentSession.target_user_name} {currentSession.target_user_surname}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Impersonato da: {currentSession.original_user_name} {currentSession.original_user_surname}
            </Typography>
            <Box mt={2}>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleEndImpersonation}
                disabled={loading}
              >
                Termina Impersonazione
              </Button>
            </Box>
          </Alert>
        )}

        {/* Filtri */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            label="Cerca utente"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Ruolo</InputLabel>
            <Select
              value={filterRole}
              label="Ruolo"
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="employee">Dipendente</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => setImpersonationDialogOpen(true)}
            disabled={!!currentSession}
          >
            Inizia Impersonazione
          </Button>
        </Box>

        {/* Lista utenti */}
        {loadingUsers ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Utente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ruolo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Dipendente</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((u) => {
                  const employee = userToEmployeeMap.get(u.id);
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        {u.nome} {u.cognome}
                        {u.is_super_admin && (
                          <Chip label="Super Admin" color="secondary" size="small" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={u.role === 'admin' ? 'Admin' : 'Dipendente'}
                          color={u.role === 'admin' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {employee ? (
                          <Chip label={employee.full_name} color="success" size="small" />
                        ) : (
                          <Chip label="N/A" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setImpersonationDialogOpen(true);
                          }}
                          disabled={!!currentSession || u.id === user?.id}
                        >
                          Accedi
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Dialog conferma impersonazione */}
        <Dialog open={impersonationDialogOpen} onClose={() => setImpersonationDialogOpen(false)}>
          <DialogTitle>Conferma Impersonazione</DialogTitle>
          <DialogContent>
            {selectedUserId && (
              <Typography>
                Vuoi iniziare a impersonare{' '}
                <strong>
                  {users.find(u => u.id === selectedUserId)?.nome}{' '}
                  {users.find(u => u.id === selectedUserId)?.cognome}
                </strong>
                ?
              </Typography>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              Tutte le operazioni verranno eseguite come se fossi questo utente.
              La sessione rimarrà attiva finché non la terminerai esplicitamente.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImpersonationDialogOpen(false)}>Annulla</Button>
            <Button
              onClick={handleStartImpersonation}
              variant="contained"
              disabled={loading || !selectedUserId}
            >
              {loading ? <CircularProgress size={24} /> : 'Conferma'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ImpersonationManager;

